/**
 * 语言配置文件
 *
 * 该文件定义了系统支持的所有语言及其相关配置
 * 新增语言时，只需在此数组中添加一个新的语言配置对象，然后在 locales/ 目录下创建对应的翻译文件即可
 *
 * 配置说明：
 * - code: 语言代码，遵循 ISO 639-1 标准（如 en, ko, id 等）
 * - label: 语言标签，用于在语言选择器中显示
 * - hiddenPaths: 该语言下需要隐藏的路由路径数组（可选）
 * - links: 该语言特定的外部链接配置
 *   - appleStore: Apple App Store 链接（根据地区不同）
 *   - terms: 服务条款页面链接
 *   - privacy: 隐私政策页面链接
 */

/**
 * 语言特定的外部链接配置
 */
export interface LanguageLinks {
  /** Apple App Store 链接 */
  appleStore: string;
  /** 服务条款页面链接 */
  terms: string;
  /** 隐私政策页面链接 */
  privacy: string;
}

/**
 * 语言配置接口
 */
export interface LanguageConfig {
  /** 语言代码（ISO 639-1） */
  code: string;
  /** 显示标签 */
  label: string;
  /** 该语言下需要隐藏的路由路径数组 */
  hiddenPaths: string[];
  /** 语言特定的外部链接配置 */
  links: LanguageLinks;
  /** 是否使用自定义字体（ppgatwick），默认 true；设为 false 则使用系统字体 */
  useCustomFont?: boolean;
  /** 语言代码别名，用于兼容旧路由 */
  aliases?: string[];
}

/**
 * 支持的语言列表
 */
const languages: LanguageConfig[] = [
  {
    code: 'en', // 英语
    label: 'EN', // 显示标签
    hiddenPaths: [], // 英语版本显示所有导航
    useCustomFont: true, // 使用自定义字体
    links: {
      // 英语版本使用中国区 App Store
      appleStore: 'https://apps.apple.com/us/app/block-blast/id1617391485',
      // 英语版本的服务条款（全球通用）
      terms: 'https://hungrystudio.com/serviceus.html',
      // 英语版本的隐私政策（全球通用）
      privacy: 'https://hungrystudio.com/privacyus.html',
    },
  },
  {
    code: 'ko', // 韩语
    label: 'KO', // 显示标签
    // 韩语版本隐藏博客和媒体页面
    hiddenPaths: ['/blogs', '/media', '/advertisement'],
    useCustomFont: true, // 使用自定义字体
    links: {
      // 韩语版本使用韩国区 App Store（带韩文名称）
      appleStore:
        'https://apps.apple.com/kr/app/%EB%B8%94%EB%A1%9D-%EB%B8%94%EB%9D%BC%EC%8A%A4%ED%8A%B8-block-blast/id1617391485',
      // 韩语版本的服务条款（韩国本地化）
      terms: 'https://www.hungrystudio.com/KR/serviceus.html',
      // 韩语版本的隐私政策（韩国本地化）
      privacy: 'https://www.hungrystudio.com/KR/privacyus.html',
    },
  },
  {
    code: 'id', // 印尼语
    label: 'ID', // 显示标签
    // 印尼语版本隐藏博客和媒体页面
    hiddenPaths: ['/blogs', '/media', '/advertisement'],
    useCustomFont: true, // 使用自定义字体
    links: {
      // 印尼语版本使用中国区 App Store
      appleStore: 'https://apps.apple.com/id/app/block-blast/id1617391485?l=id',
      // 印尼语版本的服务条款（使用全球通用版本）
      terms: 'https://www.hungrystudio.com/ID/serviceus.html',
      // 印尼语版本的隐私政策（使用全球通用版本）
      privacy: 'https://www.hungrystudio.com/ID/privacyus.html',
    },
  },
  {
    code: 'vn', // 越南语
    label: 'VN', // 显示标签
    aliases: ['vi'], // 兼容旧的 vi 路由
    // 越南语版本隐藏博客和媒体页面
    hiddenPaths: ['/blogs', '/media', '/advertisement'],
    useCustomFont: false, // 使用系统字体（ppgatwick 不支持越南语特殊字符）
    links: {
      // 越南语版本使用中国区 App Store
      appleStore: 'https://apps.apple.com/us/app/block-blast/id1617391485?l=vi',
      // 越南语版本的服务条款（越南本地化）
      terms: 'https://www.hungrystudio.com/VN/serviceus.html',
      // 越南语版本的隐私政策（越南本地化）
      privacy: 'https://www.hungrystudio.com/VN/privacyus.html',
    },
  },
];

/**
 * 导出语言配置数组
 * 该数组会被 utils/language.ts 中的工具函数使用
 */
export default languages;
